/**
 * AtAGlanceStrip — single-row strip that fuses the top universal daily
 * questions into one band, between the Hero chips and the service grid.
 *
 * Pattern lifted from Google Pixel "At a Glance":
 *   🔔 3 new   ·   🕌 Asr in 2h12   ·   📅 Eid al-Adha in 28d
 *
 * Each segment is its own clickable region. The strip fails open: if a
 * data source is missing (no auth token → no notifs, no location → no
 * prayer), that segment quietly hides instead of breaking the strip.
 *
 * Per CLAUDE.md "Home Page Anti-Regression Rule #1": this component
 * renders synchronously with cached data only. The hero re-render path
 * already updates the strip when location/weather/prayer state changes.
 */

import type { AweniContext } from '../../types';
import { prayerTimeService } from '../../services/PrayerTimeService';
import { getCurrentSpecialDay, isJumuahDay } from '../../services/IslamicCalendar';
import { getLocationService } from '../../services/LocationService';

interface Segment {
  icon: string;
  primary: string;
  secondary?: string;
  href?: string;
  onClick?: () => void;
  ariaLabel: string;
}

export class AtAGlanceStrip {
  private root: HTMLElement | null = null;
  private deps: { ctx: AweniContext };
  private locUnsub: (() => void) | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;

  constructor(deps: { ctx: AweniContext }) {
    this.deps = deps;
  }

  mount(parent: HTMLElement): void {
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('section');
    root.className = 'aweni-glance-strip';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', this.deps.ctx.i18n.t('glance_strip_aria') || 'At a glance');
    parent.appendChild(root);
    this.root = root;
    this.render();

    // Re-render when the user changes location — prayer countdown changes
    // and the next-prayer label may shift. Also serves as recovery if
    // prayer-time-service wasn't synced yet on the very first render.
    this.locUnsub = getLocationService().onChange(() => this.render());

    // Minute tick — countdown labels ("in 2h 43m") tick down naturally
    // every minute, AND it acts as a self-healing re-render in case prayer
    // service had no data on the first render but does later.
    this.tick = setInterval(() => this.render(), 60_000);
  }

  /** Public hook to force an immediate re-render — used by HomeModule
   *  after it syncs prayer-time-service following a city change. */
  refresh(): void {
    this.render();
  }

  unmount(): void {
    this.locUnsub?.();
    this.locUnsub = null;
    if (this.tick !== null) {
      clearInterval(this.tick);
      this.tick = null;
    }
    this.root?.remove();
    this.root = null;
  }

  private render(): void {
    if (!this.root) return;
    const segments = this.collectSegments();
    if (segments.length === 0) {
      // No data → hide the strip entirely (don't draw an empty band).
      this.root.style.display = 'none';
      return;
    }
    this.root.style.display = '';

    this.root.innerHTML = segments.map((s, i) => `
      ${i > 0 ? '<span class="aweni-glance-sep" aria-hidden="true">·</span>' : ''}
      ${s.href
        ? `<a class="aweni-glance-seg" href="${s.href}" aria-label="${escAttr(s.ariaLabel)}">
             ${this.segInner(s)}
           </a>`
        : `<button type="button" class="aweni-glance-seg" data-action="seg-${i}" aria-label="${escAttr(s.ariaLabel)}">
             ${this.segInner(s)}
           </button>`}
    `).join('');

    // Wire click handlers for non-href segments.
    segments.forEach((s, i) => {
      if (s.onClick && !s.href) {
        const btn = this.root!.querySelector<HTMLElement>(`[data-action="seg-${i}"]`);
        btn?.addEventListener('click', () => s.onClick?.());
      }
    });
  }

  private segInner(s: Segment): string {
    return `
      <span class="aweni-glance-icon" aria-hidden="true">${s.icon}</span>
      <span class="aweni-glance-text">
        <span class="aweni-glance-primary">${escHtml(s.primary)}</span>
        ${s.secondary ? `<span class="aweni-glance-secondary">${escHtml(s.secondary)}</span>` : ''}
      </span>
    `;
  }

  private collectSegments(): Segment[] {
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const segments: Segment[] = [];

    // 1) Notifications — only if user is authenticated AND has unread.
    // The AweniContext type from src/types isn't updated with the runtime
    // `notifications` field (it's added by main.ts). Read via a defensive
    // cast so we don't drag a type-system change into a UI patch.
    const ctxAny = this.deps.ctx as unknown as { notifications?: { unreadCount: number } };
    const notifs = ctxAny.notifications;
    if (notifs && notifs.unreadCount > 0) {
      segments.push({
        icon: '🔔',
        primary: String(notifs.unreadCount),
        secondary: notifs.unreadCount === 1
          ? (t('glance_strip_notif_one') || 'new')
          : (t('glance_strip_notif_many') || 'new'),
        ariaLabel: `${notifs.unreadCount} ${t('glance_strip_notif_aria') || 'new notifications'}`,
        // The notification drawer lives in the TopBar — toggle via DOM event.
        onClick: () => {
          const ev = new CustomEvent('aweni:open-notifications');
          window.dispatchEvent(ev);
        },
      });
    }

    // 2) Next prayer — only when we have coordinates.
    const loc = getLocationService().getState();
    if (loc.lat !== null && loc.lng !== null) {
      try {
        const next = prayerTimeService.getNextPrayer();
        if (next) {
          const prayerName = t(`prayer_${next.name}`) || next.name;
          const minsTotal = Math.max(0, Math.round(next.minutesUntil));
          const hours = Math.floor(minsTotal / 60);
          const mins = minsTotal % 60;
          const compact = hours > 0
            ? t('home_hero_in_hm', { h: String(hours), m: String(mins).padStart(2, '0') })
            : t('home_hero_in_m', { m: String(mins) });
          const inPrefix = t('glance_strip_in_prefix') || 'in ';
          segments.push({
            icon: '🕌',
            primary: prayerName,
            secondary: `${inPrefix}${compact}`,
            href: '#/prayer',
            ariaLabel: `${prayerName} ${inPrefix}${compact}`,
          });
        }
      } catch { /* prayer service unavailable — skip segment */ }
    }

    // 3) Special Islamic day OR Friday Jumu'ah marker — quick spiritual
    //    awareness signal at a glance. Skips silently when neither applies.
    try {
      const special = getCurrentSpecialDay();
      if (special) {
        segments.push({
          icon: '📅',
          primary: t(`islamic_day_${special.id}`) || special.id,
          secondary: t('glance_strip_today') || 'today',
          href: '#/quran/calendar',
          ariaLabel: special.id,
        });
      } else if (isJumuahDay()) {
        segments.push({
          icon: '📅',
          primary: t('islamic_day_jumuah') || 'Jumu\'ah',
          secondary: t('glance_strip_today') || 'today',
          href: '#/prayer',
          ariaLabel: 'Jumu\'ah',
        });
      }
    } catch { /* islamic calendar service unavailable — skip */ }

    return segments;
  }

  private injectStyles(): void {
    if (document.getElementById('aweni-glance-strip-styles')) return;
    const style = document.createElement('style');
    style.id = 'aweni-glance-strip-styles';
    style.textContent = `
      .aweni-glance-strip {
        display: flex; align-items: center; gap: 6px;
        padding: 10px 14px;
        margin: 0 0 12px;
        background: var(--card-bg, #fff);
        border: 1px solid var(--border, #E5E7EB);
        border-radius: 14px;
        overflow-x: auto;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
      }
      .aweni-glance-strip::-webkit-scrollbar { display: none; }
      .dark .aweni-glance-strip {
        background: rgba(255,255,255,0.04);
        border-color: rgba(255,255,255,0.10);
      }
      .aweni-glance-seg {
        flex-shrink: 0;
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 8px;
        background: transparent;
        border: 1px solid transparent;
        border-radius: 10px;
        color: var(--text-primary, #1E293B);
        font: inherit; font-size: 12px; font-weight: 600;
        text-decoration: none; cursor: pointer;
        transition: background 120ms ease, border-color 120ms ease;
      }
      .aweni-glance-seg:hover {
        background: rgba(10,10,10,0.04);
        border-color: rgba(10,10,10,0.08);
      }
      .dark .aweni-glance-seg:hover {
        background: rgba(255,255,255,0.06);
        border-color: rgba(255,255,255,0.10);
      }
      .aweni-glance-seg:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A); outline-offset: 1px;
      }
      .aweni-glance-icon { font-size: 14px; line-height: 1; }
      .aweni-glance-text { display: inline-flex; gap: 4px; align-items: baseline; }
      .aweni-glance-primary { color: var(--text-primary, #1E293B); }
      .aweni-glance-secondary {
        color: var(--text-secondary, #6B7280);
        font-weight: 500;
      }
      .dark .aweni-glance-primary { color: #F1F5F9; }
      .dark .aweni-glance-secondary { color: #94A3B8; }
      .aweni-glance-sep {
        flex-shrink: 0;
        color: var(--text-secondary, #9CA3AF);
        font-size: 12px;
        opacity: 0.5;
      }
    `;
    document.head.appendChild(style);
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
